import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityAssetConverterService} from './services/entity-asset-converter.service';
import {EntityAssetService} from './services/entity-asset.service';
import {EntityAssetViewModel} from './view-models/entity-asset.view-model';
import {ModalService} from '@shared/services/modal.service';
import {EntityAssetDetailsModalComponent} from './entity-asset-details-modal/entity-asset-details-modal.component';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {Keys} from '@shared/keys';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {EntityAssetCardContainerComponent} from './entity-asset-card-container/entity-asset-card-container.component';

@Component({
    selector: 'app-entity-assets',
    templateUrl: './entity-assets.component.html',
    styleUrls: ['./entity-assets.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, EntityAssetCardContainerComponent]
})
export class EntityAssetsComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly entityAssetService = inject(EntityAssetService);
  private readonly clientSideDataService = inject(ClientsideDataService<EntityAssetViewModel>);
  private readonly entityAssetConverterService = inject(EntityAssetConverterService);
  private readonly modalService = inject(ModalService);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);

  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('name');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<EntityAssetViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private readonly SEARCH_FIELDS: Keys<EntityAssetViewModel> = ['portfolioNumber', 'name', 'type'];

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Assets);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const params = AppParameterMap.from(this.router);
    const entityId = params.get(PARAM_ENTITY_ID);

    this.entityAssetService.getEntityAssets(entityId).subscribe(assets => {
      const entityAssetVMs = this.entityAssetConverterService.convert(entityId, assets);
      this.clientSideDataService.initialize(entityAssetVMs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(entityAssetVMs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
    const entityAssetViewModel = dataViewModel as EntityAssetViewModel;
    this.modalService.openModal(EntityAssetDetailsModalComponent, entityAssetViewModel);
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Portfolio Number',
        dataProperty: 'portfolioNumber',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '10%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Name',
        dataProperty: 'name',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Type',
        dataProperty: 'type',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Currency',
        dataProperty: 'bookValueCurrency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '7%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Book Value',
        dataProperty: 'bookValue',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '10%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Market Value Currency',
        dataProperty: 'marketValueCurrency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '10%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Market Value',
        dataProperty: 'marketValue',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '10%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Market Value Date',
        dataProperty: 'marketValueDate',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Right,
        width: '10%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      }
    ]);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Assets, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }

}
